import { ApiResponse, PaginatedResponse } from "../../types/common";
import { StudentResult, StudentSchedule } from "../../types/exams";
import { StudentFeeParticularsForm } from "../../types/fees";
import { FetchStudentsParams, Student, StudentFee, StudentFormData, TeacherStudentsResponse } from "../../types/students";
import { axiosInstance } from "./axios";

export const studentService = {
    getAll: async (instituteId: number): Promise<Student[]> => {
        const response = await axiosInstance.get<ApiResponse<Student[]>>(`v1/Students?InstituteId=${instituteId}`);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to fetch students');
        }
        return response.data.data;
    },
    getById: async (id: number) => {
        const response = await axiosInstance.get<ApiResponse<Student>>(`v1/Students/${id}`);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to fetch student');
        }
        return response.data.data;
    },
    create: async (data: StudentFormData, instituteId: number) => {
        const formData = new FormData();

        const { profilePicture, ...requestData } = data;

        formData.append("StudentData", JSON.stringify({ ...requestData, instituteId }));

        // Append profile picture if available
        if (profilePicture) {
            formData.append("ProfilePicture", profilePicture);
        }
        const response = await axiosInstance.post<ApiResponse<number>>('v1/Students', formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to save student');
        }
    },
    update: async (id: number, data: StudentFormData, instituteId: number) => {
        
        const formData = new FormData();

        const { profilePicture, ...requestData } = data;

        formData.append("StudentData", JSON.stringify({ ...requestData, id, instituteId }));

        // Append profile picture if available
        if (profilePicture) {
            formData.append("ProfilePicture", profilePicture);
        }

        const response = await axiosInstance.put<ApiResponse<number>>('v1/Students', formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to save student');
        }
    },
    fetchStudents: async (request: FetchStudentsParams) => {
        const response = await axiosInstance.get<PaginatedResponse<Student>>(`v1/Students`, {
            params: {
                ...request,
                StudentName: request?.name,
            }
        });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to fetch students');
        }
        return response.data;
    },
    getStudentFee: async (studentId: number) => {
        const response = await axiosInstance.get<ApiResponse<StudentFee>>(`v1/Fees/Student/${studentId}`);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to fetch students');
        }
        return response.data.data;
    },
    saveStudentFeeDetails: async (data: StudentFeeParticularsForm, instituteId: number) => {
        const response = await axiosInstance.put<ApiResponse<StudentFeeParticularsForm>>(`v1/Fees/Student/UpdateFeeDetails`,{
            ...data,
            instituteId
        });
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'failed to update student fee details.');
        }
    },
    getResults: async (studentId : number): Promise<StudentResult[]> => {
         try {
            const response = await axiosInstance.get<ApiResponse<StudentResult[]>>(`v1/exams/schedules/student-results`, {params: { studentId },});
            
            return response.data.data;
            } catch (error) {
              console.error("Error fetching exams:", error);
              throw error;
            }
    },
    getExamResult: async (examId: number, studentId: number) => {
        try {
            const response = await axiosInstance.get<ApiResponse<StudentResult>>(`v1/exams/schedules/students/${studentId}/results/${examId}`);
            
            return response.data.data;
            } catch (error) {
              console.error("Error fetching exams:", error);
              throw error;
            }
    },
    getSchedules: async (studentId: number) => {
        try {
            const response = await axiosInstance.get<ApiResponse<StudentSchedule[]>>(`v1/exams/schedules/students-schedules`, { params: { studentId, showPrevious: true } });
            
            return response.data.data;
            } catch (error) {
              console.error("Error fetching exams:", error);
              throw error;
            }
      },
    getTeacherStudents: async (): Promise<TeacherStudentsResponse> => {
        const response = await axiosInstance.get<ApiResponse<TeacherStudentsResponse>>('v1/teacher/students');
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'Failed to fetch students');
        }
        return response.data.data;
    },
    generateBiodataLetter: async (id: number): Promise<{ fileName: string; url: string }> => {
        const response = await axiosInstance.get<ApiResponse<{ fileName: string; url: string }>>(`v1/Students/${id}/generate-biodata-letter`);
        if (!response.data.succeeded) {
            throw new Error(response.data.messages?.[0] || 'Failed to generate biodata letter');
        }
        return response.data.data;
    },
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const studentResults: StudentResult[] = [
  {
    examId: 5,
    examName: 'Second Quarterly Assessment',
    examType: 'Quarterly',
    examDate: '2024-09-22',
    studentId: 1,
    status: 'Published',
    overallMarks: 425,
    totalMarks: 500,
    percentage: 85,
    grade: 'A',
    rank: 3,
    classAverage: 78.5,
    subjects: [
      {
        subject: 'Math',
        marksObtained: 85,
        maxMarks: 100,
        grade: 'A',
        remarks: 'Excellent problem solving skills',
        teacherName: 'John Smith',
        classAverage: 75,
      },
      {
        subject: 'English',
        marksObtained: 90,
        maxMarks: 100,
        grade: 'A+',
        remarks: 'Outstanding writing and comprehension',
        teacherName: 'Sarah Johnson',
        classAverage: 82,
      },
      {
        subject: 'Science',
        marksObtained: 88,
        maxMarks: 100,
        grade: 'A',
        remarks: 'Good understanding of concepts',
        teacherName: 'Michael Brown',
        classAverage: 80,
      },
      {
        subject: 'Social Studies',
        marksObtained: 82,
        maxMarks: 100,
        grade: 'A',
        remarks: 'Well presented answers',
        teacherName: 'Emily Davis',
        classAverage: 76,
      },
      {
        subject: 'Computer',
        marksObtained: 80,
        maxMarks: 100,
        grade: 'A',
        remarks: 'Good programming logic',
        teacherName: 'Robert Wilson',
        classAverage: 74,
      },
    ],
  },
  {
    examId: 7,
    examName: 'Mid-Year Evaluation',
    examType: 'Mid-term',
    examDate: '2024-07-15',
    studentId: 1,
    status: 'Published',
    overallMarks: 390,
    totalMarks: 500,
    percentage: 78,
    grade: 'B+',
    rank: 5,
    classAverage: 72.3,
    subjects: [
      {
        subject: 'Math',
        marksObtained: 75,
        maxMarks: 100,
        grade: 'B+',
        remarks: 'Good effort, practice more word problems',
        teacherName: 'John Smith',
        classAverage: 70,
      },
      {
        subject: 'English',
        marksObtained: 85,
        maxMarks: 100,
        grade: 'A',
        remarks: 'Excellent language skills',
        teacherName: 'Sarah Johnson',
        classAverage: 78,
      },
      {
        subject: 'Science',
        marksObtained: 80,
        maxMarks: 100,
        grade: 'A',
        remarks: 'Very good practical work',
        teacherName: 'Michael Brown',
        classAverage: 75,
      },
      {
        subject: 'Social Studies',
        marksObtained: 78,
        maxMarks: 100,
        grade: 'B+',
        remarks: 'Good knowledge of history',
        teacherName: 'Emily Davis',
        classAverage: 71,
      },
      {
        subject: 'Computer',
        marksObtained: 72,
        maxMarks: 100,
        grade: 'B',
        remarks: 'Needs improvement in coding',
        teacherName: 'Robert Wilson',
        classAverage: 68,
      },
    ],
  },
  {
    examId: 8,
    examName: 'First Quarterly Test',
    examType: 'Quarterly',
    examDate: '2024-04-10',
    studentId: 1,
    status: 'Published',
    overallMarks: 380,
    totalMarks: 500,
    percentage: 76,
    grade: 'B+',
    rank: 7,
    classAverage: 70.5,
    subjects: [
      {
        subject: 'Math',
        marksObtained: 72,
        maxMarks: 100,
        grade: 'B',
        remarks: 'Focus on geometry concepts',
        teacherName: 'John Smith',
        classAverage: 68,
      },
      {
        subject: 'English',
        marksObtained: 88,
        maxMarks: 100,
        grade: 'A',
        remarks: 'Excellent vocabulary',
        teacherName: 'Sarah Johnson',
        classAverage: 80,
      },
      {
        subject: 'Science',
        marksObtained: 75,
        maxMarks: 100,
        grade: 'B+',
        remarks: 'Good lab work',
        teacherName: 'Michael Brown',
        classAverage: 72,
      },
      {
        subject: 'Social Studies',
        marksObtained: 70,
        maxMarks: 100,
        grade: 'B',
        remarks: 'Read more about geography',
        teacherName: 'Emily Davis',
        classAverage: 67,
      },
      {
        subject: 'Computer',
        marksObtained: 75,
        maxMarks: 100,
        grade: 'B+',
        remarks: 'Good understanding of basics',
        teacherName: 'Robert Wilson',
        classAverage: 70,
      },
    ],
  },
  {
    examId: 9,
    examName: 'Annual Final Examination',
    examType: 'Final',
    examDate: '2024-12-20',
    studentId: 1,
    status: 'Pending',
    overallMarks: 0,
    totalMarks: 500,
    percentage: 0,
    grade: '',
    classAverage: 0,
    subjects: [],
  },
];
export const studentSchedules: StudentSchedule[] = [
  {
    examId: 1,
    examName: 'First Term Examination 2024',
    examType: 'Mid-term',
    startDate: '2025-12-01',
    endDate: '2025-12-15',
    subjects: [
      {
        subject: 'Math',
        date: '2025-12-01',
        startTime: '09:00',
        duration: 60,
        maxMarks: 100,
        status: 'Upcoming',
      },
      {
        subject: 'English',
        date: '2025-12-02',
        startTime: '09:00',
        duration: 60,
        maxMarks: 100,
        status: 'Upcoming',
      },
      {
        subject: 'Science',
        date: '2025-12-03',
        startTime: '09:00',
        duration: 60,
        maxMarks: 100,
        status: 'Upcoming',
      },
      {
        subject: 'Social Studies',
        date: '2025-12-04',
        startTime: '09:00',
        duration: 60,
        maxMarks: 100,
        status: 'Upcoming',
      },
      {
        subject: 'Art',
        date: '2025-12-05',
        startTime: '09:00',
        duration: 90,
        maxMarks: 50,
        status: 'Upcoming',
      },
    ],
  },
  {
    examId: 2,
    examName: 'Mathematics Unit Test',
    examType: 'Unit Test',
    startDate: '2025-12-20',
    endDate: '2025-12-20',
    subjects: [
      {
        subject: 'Math',
        date: '2025-12-20',
        startTime: '09:00',
        duration: 45,
        maxMarks: 50,
        status: 'Upcoming',
      },
    ],
  },
];
